import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Backdrop,
  BaseTextFieldController,
  Form,
  FormGroup,
  FormCancelButton,
  FormSubmitButton,
  MediaUploadZone,
  FormAttachments,
  RichEditorController,
  SelectController,
  NewConfirmDialog,
} from 'components';
import { NOTIFICATION_FIELDS_LENGTH } from 'constants/fieldsLength';
import { useWindowSize } from 'hooks';
import { textFieldStyles, textFieldMobileStyles, richEditorStyles } from 'styles';
import { User } from 'types';

import { useCreateNotificationForm } from './hooks';
import { descriptionFieldRules, titleFieldRules } from './rules';
import { FormAndPreviewW, FromContainer, LabelTitle } from './styled';

interface CreateNotificationFormProps {
  usersList: User[];
  onClose?: () => void;
}

export function CreateNotificationForm({
  usersList,
  onClose,
}: CreateNotificationFormProps): ReactElement {
  const { t } = useTranslation();

  const {
    control,
    handleUsersChange,
    handleFormSubmit,
    handleUploadAll,
    attachedImages,
    attachedDocs,
    attachedVideos,
    handleDeleteAttachment,
    usersOptions,
    isLoading,
    isOpen,
    progress,
    handleSubmitConfirmModal,
    handleCloseConfirmModal,
  } = useCreateNotificationForm({ usersList, onClose });

  const navigate = useNavigate();
  const { isMobile } = useWindowSize();

  const formGroupMarginBottom = isMobile ? 8 : 36;
  const fieldStyles = isMobile ? textFieldMobileStyles : textFieldStyles;

  return (
    <>
      {isLoading && <Backdrop progress={progress} />}

      <FormAndPreviewW>
        <FromContainer>
          <Form onSubmit={handleFormSubmit}>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <LabelTitle>{t('notifications.create.form.placeholders.title')}</LabelTitle>
              <BaseTextFieldController
                name="title"
                control={control}
                placeholder={t('notifications.create.form.placeholders.title')}
                sx={fieldStyles}
                maxLength={NOTIFICATION_FIELDS_LENGTH.title.max}
                rules={titleFieldRules}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <LabelTitle>{t('notifications.create.form.placeholders.list')}</LabelTitle>
              <SelectController
                name="users"
                control={control}
                placeholder={t('notifications.create.form.placeholders.list')}
                options={usersOptions}
                multipleSelect={true}
                type="notification"
                handleChange={handleUsersChange}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom} row>
              <LabelTitle>{t('notifications.create.form.placeholders.description')}</LabelTitle>
              <RichEditorController
                name="description"
                control={control}
                placeholder={t('notifications.create.form.placeholders.description')}
                maxLength={NOTIFICATION_FIELDS_LENGTH.description.max}
                rules={descriptionFieldRules}
                sx={richEditorStyles(isMobile)}
              />

              {isMobile && (
                <FormAttachments
                  attachedImages={attachedImages}
                  attachedDocs={attachedDocs}
                  attachedVideos={attachedVideos}
                  handleDeleteAttachment={handleDeleteAttachment}
                />
              )}
              <MediaUploadZone onUpload={(files) => handleUploadAll(files)} />
            </FormGroup>

            {!isMobile && (
              <FormAttachments
                attachedImages={attachedImages}
                attachedDocs={attachedDocs}
                attachedVideos={attachedVideos}
                handleDeleteAttachment={handleDeleteAttachment}
              />
            )}
            {!isMobile && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  marginTop: '32px',
                }}
              >
                <FormCancelButton
                  variant="outlined"
                  onClick={() => (onClose ? onClose() : navigate(-1))}
                >
                  {t('common.cancel')}
                </FormCancelButton>
                <FormSubmitButton type="submit">{t('common.create')}</FormSubmitButton>
              </div>
            )}

            {isMobile && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  marginTop: '16px',
                }}
              >
                <FormCancelButton
                  variant="outlined"
                  onClick={() => (onClose ? onClose() : navigate(-1))}
                >
                  {t('common.cancel')}
                </FormCancelButton>
                <FormSubmitButton onClick={handleFormSubmit}>{t('common.create')}</FormSubmitButton>
              </div>
            )}
          </Form>
        </FromContainer>
      </FormAndPreviewW>

      {isOpen && (
        <NewConfirmDialog
          subTitle={t('notifications.create.modal.exit')}
          title={t('notifications.create.modal.title')}
          open={isOpen}
          onClose={handleCloseConfirmModal}
          onConfirm={handleSubmitConfirmModal}
          submitLabel={t('notifications.create.modal.successButton')}
          cancelLabel={t('notifications.create.modal.cancelButton')}
        />
      )}
    </>
  );
}
